import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Erzeuger} from "../../api";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";

export type ErzeugerFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<ErzeugerFilter>, "filter"> &
    Partial<Pick<DataRequest<ErzeugerFilter>, "filter">>;

const tableName: string = "erzeuger"

export default function ErzeugerDataTable({...input}: Props) {
    const request = useDataRequest<ErzeugerFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <ErzeugerResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<ErzeugerFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<ErzeugerFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-erzeuger");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-erzeuger");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function ErzeugerResults({
                             request,
                             setFilteredState,
                         }: {
    request: DataRequestState<ErzeugerFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<ErzeugerFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadErzeuger: DataLoader<ErzeugerFilter, Erzeuger> = useCallback(
        async (params) => (await getApi()).erzeuger.sucheErzeuger({...params}),
        [getApi]
    );

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadErzeuger}
            onRowClick={(row) => {
                navigate("/erzeuger/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Erzeuger>> {
    const {t: translation} = useT("customer");

    return useMemo(
        () => [
            {
                field: "datum",
                headerName: capitalize(translation("date")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "nummer",
                headerName: capitalize(translation("number")),
            },
            {
                field: "name",
                headerName: capitalize(translation("name")),
                flex: 5,
            },
        ],
        [translation]
    );
}
